import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery, useQueryClient } = ReactQueryV5;
import { useMindlerApi } from "api/apiv2";
import { usePostTreatmentStatus } from "api/usePatientQueries";
import CustomModal from "components/lib/customModal";
import { TreatmentStatusDropdDown } from "components/treatmentStatusDropdown";
import { Typography } from "components/typography";
import { useDemo } from "contexts/DemoContext";
import CONSTANTS from "core/constants";
import { PropsWithChildren, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@mindlercare/ui-web";
import { Option, PostObject, Props } from "./patientTreatmentStatusModal.types";

const PatientTreatmentStatusModal = ({
  open,
  onClose,
  patientUserId,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation();
  const [currentSelectedOption, setCurrentSelectedOption] =
    useState<Option | null>(null);
  const isDemoToggled = useDemo();
  const apiv2 = useMindlerApi();
  const queryClient = useQueryClient();
  const { data: patient } = useQuery(
    apiv2.patients.getByUserId.queryOptions(
      {
        patientUserId: Number(patientUserId),
      },
      {
        enabled: !!patientUserId,
      },
    ),
  );
  const { mutateAsync: postTreatmentStatus } = usePostTreatmentStatus();

  const treatmentEvent =
    currentSelectedOption?.type === CONSTANTS.ACTIVE_TREATMENT.IN_TREATMENT
      ? CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_START
      : CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_END;

  const handleOnClose = () => {
    setCurrentSelectedOption(null);
    onClose();
  };

  const handleTreatmentStatusUpdate = async () => {
    if (!patient?.userId) return;
    const data: PostObject = {
      patientUserId: patient.userId,
      type: treatmentEvent,
      ...(treatmentEvent === CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_END && {
        endTreatmentReason: currentSelectedOption?.type,
      }),
    };

    try {
      await postTreatmentStatus(data);
      queryClient.invalidateQueries(
        apiv2.patients.getByUserId.queryFilter({
          patientUserId: patient.userId,
        }),
      );
      handleOnClose();
    } catch (error) {
      handleOnClose();
      return error;
    }
  };

  return (
    <>
      <CustomModal.Wrapper
        onClose={handleOnClose}
        open={open}
        titleText={t("TREATMENT_SET_TREATMENT_STATUS")}
        size="large"
      >
        <div data-testid="treatmentStatusModal" />
        <CustomModal.Row noPadding>
          <CustomModal.Column>
            <Typography
              variant="body-x-small"
              color="GRAY_DARK"
              style={{ display: "block", marginBottom: "0.5rem" }}
            >
              {t("NAME")}
            </Typography>
            {!isDemoToggled
              ? `${patient?.firstName} ${patient?.lastName}`
              : CONSTANTS.DEMO}
          </CustomModal.Column>
          <CustomModal.Column>
            <Typography
              variant="body-x-small"
              color="GRAY_DARK"
              style={{ display: "block", marginBottom: "0.5rem" }}
            >
              {t("USER_ID")}
            </Typography>
            {!isDemoToggled ? patient?.userId : CONSTANTS.DEMO}
          </CustomModal.Column>
        </CustomModal.Row>
        <CustomModal.Row noPadding>
          <CustomModal.Column fullWidth noMargin>
            <TreatmentStatusDropdDown
              patientUserId={patient?.userId}
              currentSelectedOption={currentSelectedOption}
              onChange={setCurrentSelectedOption}
            />
          </CustomModal.Column>
        </CustomModal.Row>
        <CustomModal.ButtonRow marginTop={15}>
          <Button
            variant="secondary"
            onClick={handleOnClose}
            testId="treatmentStatusCancel"
          >
            {t("TREATMENT_CANCEL")}
          </Button>

          <Button
            onClick={handleTreatmentStatusUpdate}
            variant="primary"
            testId="treatmentStatusApply"
          >
            {t("TREATMENT_APPLY")}
          </Button>
        </CustomModal.ButtonRow>
      </CustomModal.Wrapper>
    </>
  );
};

export default PatientTreatmentStatusModal;
